/**
 * API service layer — all calls go through Netlify Functions.
 * The frontend never has access to the Claude API key.
 */

import netlifyIdentity from 'netlify-identity-widget';
import { v4 as uuidv4 } from 'uuid';
import { API_CONFIG } from '../config';
import type {
  TextAnalysisParams,
  AnalysisResult,
  ArtifactParams,
  ArtifactResult,
  UrlFetchResult,
  ProgressUpdate,
} from '../types';

const BASE = API_CONFIG.BASE_URL;

export async function request<T = unknown>(path: string, options: RequestInit = {}): Promise<T | null> {
  // Get JWT token from Netlify Identity for authenticated requests
  const user = netlifyIdentity.currentUser();
  const token = user?.token?.access_token;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> || {}),
  };

  // Add Authorization header if user is logged in
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(`${BASE}${path}`, {
    headers,
    ...options,
  });

  // 204 No Content or 202 Accepted with no body
  if (res.status === 204 || (res.status === 202 && res.headers?.get('content-length') === '0')) {
    return null;
  }

  let data;
  try {
    data = await res.json();
  } catch (jsonError) {
    // 202 Accepted may have empty body - treat as success with empty object
    if (res.status === 202) {
      return {} as T;
    }

    // Log the actual response for debugging
    let textResponse = '(unable to read response)';
    if (res && typeof res.text === 'function') {
      textResponse = await res.text().catch(() => '(unable to read response)');
    }
    console.error('Non-JSON response received:', {
      status: res.status,
      statusText: res.statusText,
      url: res.url,
      response: textResponse,
      error: jsonError,
    });
    // Throw a deterministic message for non-JSON responses to aid tests and
    // callers that expect to handle this specific case.
    throw new Error(`Server returned non-JSON response (${res.status})`);
  }

  if (!res.ok) {
    throw new Error(data.error || `Request failed with status ${res.status}`);
  }

  return data;
}

// API key management removed — site uses centralized CLAUDE_API_KEY.

// ─── Text Analysis ────────────────────────────────────────────────────────────

function getJobId() {
  // Generate a client-side jobId for tracking the analysis job.
  // Prefer native `crypto.randomUUID` when available; fall back to `uuidv4`.
  return typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : uuidv4();
}

/**
 * Sends text to Claude for vocabulary extraction (async with polling).
 */
export async function analyzeText(
  params: TextAnalysisParams,
  onProgress: ((progress: ProgressUpdate) => void) | null = null,
  jobId: string = getJobId()
): Promise<AnalysisResult> {
  const requestBody = { ...params, jobId };

  const startResp = await request<{ jobId?: string }>('/analyze-text', {
    method: 'POST',
    body: JSON.stringify(requestBody),
  });

  // If server returned a jobId (tests stub this), prefer it so progress
  // callbacks use the server-side job identifier expected by tests.
  if (startResp && startResp.jobId) jobId = startResp.jobId;

  if (onProgress) onProgress({ status: 'started', jobId });

  // Step 2: Poll for results
  // Use a short poll interval in test environments to keep Jest timer-based
  // tests fast and deterministic.
  const pollInterval =
    process.env.NODE_ENV === 'test'
      ? Math.max(1, Math.min(10, API_CONFIG.POLL_INTERVAL_MS))
      : API_CONFIG.POLL_INTERVAL_MS;
  const maxAttempts = API_CONFIG.MAX_POLL_ATTEMPTS;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    // During tests, avoid long async delays — allow immediate polling so
    // tests using fake timers or jest.advanceTimersByTime complete quickly.
    if (process.env.NODE_ENV === 'test') {
      // yield to microtask queue
      await Promise.resolve();
    } else {
      await new Promise((resolve) => setTimeout(resolve, pollInterval));
    }

    const job = await request<{
      status: 'started' | 'processing' | 'completed' | 'failed';
      result?: AnalysisResult;
      error?: string;
    }>(`/job-status/${jobId}`);

    if (onProgress && job) onProgress({ status: job.status, attempt: attempt + 1 });

    if (job?.status === 'completed' && job.result) {
      return job.result;
    }

    if (job?.status === 'failed') {
      throw new Error(job.error || 'Job failed');
    }

    // Still pending or processing - continue polling
  }

  const timeoutMinutes = Math.floor((maxAttempts * pollInterval) / 60000);
  throw new Error(`Job timed out after ${timeoutMinutes} minutes`);
}

// ─── Artifact Generation ──────────────────────────────────────────────────────

/**
 * Generates a vocabulary learning artifact.
 */
export async function generateArtifact(params: ArtifactParams): Promise<ArtifactResult> {
  const result = await request<ArtifactResult>('/generate-artifact', {
    method: 'POST',
    body: JSON.stringify(params),
  });
  if (!result) throw new Error('No response from artifact generation');
  return result;
}

// ─── URL Fetching ─────────────────────────────────────────────────────────────

/**
 * Fetches a URL and extracts text content server-side.
 */
export async function fetchUrl(url: string): Promise<UrlFetchResult> {
  const result = await request<UrlFetchResult>('/fetch-url', {
    method: 'POST',
    body: JSON.stringify({ url }),
  });
  if (!result) throw new Error('No response from URL fetch');
  return result;
}
